import { z } from 'zod';

/**
 * The kind of OS power transition observed by PlaycastService.
 * - `suspend`  — entering sleep / modern-standby / hibernate.
 * - `resume`   — returning from a suspend.
 * - `shutdown` — graceful power-off (SERVICE_CONTROL_SHUTDOWN).
 */
export type PowerEventKind = 'suspend' | 'resume' | 'shutdown';

/**
 * Best-effort classification of a `suspend` transition. Present only when
 * `event === 'suspend'`.
 */
export type SuspendKind = 'sleep' | 'modern-standby' | 'hibernate';

/**
 * Where the signal originated, for diagnostics and reliability triage.
 * - `apm`              — from PBT_APMSUSPEND/RESUME (best-effort, ~2s budget).
 * - `pre-hold-release` — emitted a few seconds before PlaycastService releases
 *                        its own sleep-preventing power hold (the reliable path).
 * - `service-shutdown` — from SERVICE_CONTROL_SHUTDOWN.
 * - `service-start`    — a `resume` re-asserted when PlaycastService's hub accepts
 *                        a client connection (its reliable "I'm up / awake" edge).
 *                        This is the recovery path for a machine that never got an
 *                        OS resume event — notably a cold boot after power loss —
 *                        so a stale `asleep` (e.g. modern-standby) is cleared once
 *                        node-service reconnects.
 * - `display-off`      — inferred `suspend` on modern-standby (S0) hosts, where the
 *                        OS never delivers PBT_APMSUSPEND to the Session-0 service.
 *                        Emitted when the display turns off and nothing is holding
 *                        the machine awake and the OS power policy will sleep.
 * - `display-on`       — the matching `resume` when the display turns back on.
 */
export type PowerEventSource =
  | 'apm'
  | 'pre-hold-release'
  | 'service-shutdown'
  | 'service-start'
  | 'display-off'
  | 'display-on';

/**
 * IPC payload emitted by PlayJector (C++) to node-service. `machineId` is added
 * later by node-service when it builds the REST report.
 */
export interface PowerTransitionPayload {
  event: PowerEventKind;
  /** Present only when `event === 'suspend'`. */
  suspendKind?: SuspendKind;
  /** GetPwrCapabilities AoAc flag (machine supports modern standby). */
  modernStandbyCapable: boolean;
  /** Whether hibernation is configured/available on the machine. */
  hibernateEnabled: boolean;
  /** ms epoch when the transition was observed on the PC. */
  occurredAtMs: number;
  source: PowerEventSource;
}

/**
 * REST body POSTed by node-service to realtime-api `/realtime/power/event`.
 * Capability flags and `source` are optional here so the endpoint tolerates
 * minimal producers.
 */
export interface PowerEventReport {
  machineId: string;
  event: PowerEventKind;
  suspendKind?: SuspendKind;
  modernStandbyCapable?: boolean;
  hibernateEnabled?: boolean;
  occurredAtMs: number;
  source?: PowerEventSource;
}

/**
 * IPC ack from node-service back to PlaycastService after it attempts to POST a
 * `powerTransition` to realtime-api. `occurredAtMs` echoes the originating
 * transition (correlation key); `accepted` is true when the cloud POST landed.
 */
export interface PowerTransitionAckPayload {
  occurredAtMs: number;
  accepted: boolean;
}

const PowerEventKindSchema = z.enum(['suspend', 'resume', 'shutdown']);
const SuspendKindSchema = z.enum(['sleep', 'modern-standby', 'hibernate']);
const PowerEventSourceSchema = z.enum([
  'apm',
  'pre-hold-release',
  'service-shutdown',
  'service-start',
  'display-off',
  'display-on',
]);

/**
 * `occurredAtMs` is an ms-epoch timestamp: it must be a finite, non-negative
 * integer. `.int()` also rejects NaN/Infinity, so this covers "finite".
 */
const OccurredAtMsSchema = z.number().int().nonnegative();

/**
 * `suspendKind` classifies a suspend transition and is meaningless for
 * `resume`/`shutdown`. Reject it unless `event === 'suspend'` so producers can't
 * ship contradictory payloads.
 */
const rejectSuspendKindOutsideSuspend = (
  value: { event: PowerEventKind; suspendKind?: SuspendKind },
  ctx: z.RefinementCtx,
): void => {
  if (value.suspendKind !== undefined && value.event !== 'suspend') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['suspendKind'],
      message: 'suspendKind is only valid when event is "suspend"',
    });
  }
};

/**
 * The display-derived sources encode a fixed transition direction: `display-off`
 * is an inferred modern-standby *suspend* and `display-on` is its paired *resume*.
 * Each therefore pairs with EXACTLY ONE event — `display-off` only with `suspend`,
 * `display-on` only with `resume` — so any other event (including `shutdown`, and
 * the swapped `resume`/`suspend`) is rejected. Non-display sources are unaffected.
 */
const rejectMismatchedDisplaySourceEvent = (
  value: { event: PowerEventKind; source?: PowerEventSource },
  ctx: z.RefinementCtx,
): void => {
  if (value.source === 'display-off' && value.event !== 'suspend') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['event'],
      message: 'display-off is an inferred suspend and only pairs with a "suspend" event',
    });
  }
  if (value.source === 'display-on' && value.event !== 'resume') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['event'],
      message: 'display-on is a resume and only pairs with a "resume" event',
    });
  }
};

export const PowerTransitionPayloadSchema = z
  .object({
    event: PowerEventKindSchema,
    suspendKind: SuspendKindSchema.optional(),
    modernStandbyCapable: z.boolean(),
    hibernateEnabled: z.boolean(),
    occurredAtMs: OccurredAtMsSchema,
    source: PowerEventSourceSchema,
  })
  .superRefine(rejectSuspendKindOutsideSuspend)
  .superRefine(rejectMismatchedDisplaySourceEvent);

export const PowerEventReportSchema = z
  .object({
    machineId: z.string().min(1),
    event: PowerEventKindSchema,
    suspendKind: SuspendKindSchema.optional(),
    modernStandbyCapable: z.boolean().optional(),
    hibernateEnabled: z.boolean().optional(),
    occurredAtMs: OccurredAtMsSchema,
    source: PowerEventSourceSchema.optional(),
  })
  .superRefine(rejectSuspendKindOutsideSuspend)
  .superRefine(rejectMismatchedDisplaySourceEvent);

export const PowerTransitionAckPayloadSchema = z.object({
  occurredAtMs: OccurredAtMsSchema,
  accepted: z.boolean(),
});
